import ERROR_ICON from '@assets/icons/error.svg'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const MAX_RETRY = 5
const RETRY_INTERVAL = 1000

const NetworkErrorFallback = ({ error, resetErrorBoundary }) => {
  const { t } = useTranslation()
  const { reset: resetQuery } = useQueryErrorResetBoundary()

  const [isFailed, setIsFailed] = useState(false)
  const retryingRef = useRef(false)
  const failedUrl = error?.url ? `/v1/asp/${error?.url}` : '/v1/asp/system/server-time'

  // 서버 연결 상태를 확인하는 비동기 함수
  const checkConnection = async () => {
    try {
      // 진행 중인 비동기 작업(특히 fetch 요청)을 중단(cancel)하기 위한 표준 Web API
      const controller = new AbortController()
      // 서버가 3초 이상 응답하지 않으면 fetch 강제 취소
      const timeoutId = setTimeout(() => controller.abort(), 3000)

      await fetch(failedUrl, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal // abortController와 연결해서 취소 가능하게 함
      })

      // fetch가 3초 안에 성공하면 abort 제거
      clearTimeout(timeoutId)
      return true
    } catch (error) {
      console.error('[checkConnection 실패]', error)
      return false
    }
  }

  useEffect(() => {
    if (retryingRef.current) return
    retryingRef.current = true

    // 재시도 카운트
    let count = 0

    const retry = async () => {
      // 서버 연결 확인
      const online = await checkConnection()
      if (online) {
        resetQuery()
        // Error Boundary 내부 상태 초기화
        resetErrorBoundary()
        return
      }

      count++

      if (count >= MAX_RETRY) {
        setIsFailed(true)
        return
      }

      setTimeout(retry, RETRY_INTERVAL)
    }

    retry()
  }, [])

  // 재시도 중
  if (!isFailed) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-8 px-16 text-center">
        <img src={ERROR_ICON} alt={t('common.errorAlt')} width={54} height={47} />
        <h2 className="text-18 mt-24 font-bold">{t('error.network.title')}</h2>
        <p className="text-12 text-navy-400">{t('error.network.reconnecting')}</p>
      </div>
    )
  }

  // 최종 실패
  return (
    <div className={'flex h-screen flex-col items-center justify-center gap-8 text-center'}>
      <img
        src={ERROR_ICON}
        alt={t('common.errorAlt')}
        width={54}
        height={47}
        className={'mr-8'}
        decoding="async"
      />
      <h2 className={'text-18 mt-24 font-bold'}>{t('error.network.title')}</h2>

      <p className={'text-12 text-navy-400'}>
        <span className={'block'}>{t('error.network.descriptionLine1')}</span>
        <span className={'block'}>{t('error.network.descriptionLine2')}</span>
      </p>

      <button
        className={'bg-navy-900 text-14 rounded-3xl px-16 py-8 text-white'}
        onClick={() => resetErrorBoundary()}
      >
        {t('common.retry')}
      </button>
    </div>
  )
}

export default NetworkErrorFallback
